import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';

const STATUSES = [
  { key: 'setup',         label: 'Em setup',      color: 'var(--text3)',   bg: 'rgba(107,114,128,0.10)' },
  { key: 'testing',       label: 'Testando',      color: 'var(--accent)',  bg: 'rgba(67,97,238,0.10)' },
  { key: 'validated',     label: 'Validado',      color: 'var(--accent-g)',bg: 'rgba(34,201,147,0.10)' },
  { key: 'scaling',       label: 'Escalando',     color: 'var(--accent-p)',bg: 'rgba(180,154,255,0.12)' },
  { key: 'not_validated', label: 'Nao validado',  color: 'var(--accent-a)',bg: 'rgba(247,166,43,0.10)' },
  { key: 'dead',          label: 'Morto',         color: 'var(--accent-r)',bg: 'rgba(245,84,107,0.10)' },
];

const centsToStr = (c) => c ? (c/100).toLocaleString('pt-BR', { style:'currency', currency:'BRL' }) : '—';

export default function ProductTests() {
  const [products, setProducts] = useState([]);
  const [nichoFilter, setNichoFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [selected, setSelected] = useState(null); // full product with checklist+metrics

  const load = () => {
    const p = new URLSearchParams();
    if (nichoFilter) p.set('nicho', nichoFilter);
    apiFetch(`/api/product-tests?${p}`)
      .then(d => {
        let items = d.items || [];
        if (search.trim()) {
          const q = search.toLowerCase();
          items = items.filter(x => (x.name || '').toLowerCase().includes(q) || (x.nicho || '').toLowerCase().includes(q));
        }
        setProducts(items);
      })
      .catch(e => setError(String(e.message || e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [nichoFilter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);

  const create = async (e) => {
    e.preventDefault();
    setError(null);
    const n = newName.trim();
    if (!n) { setError('Digite o nome do produto antes de criar.'); return; }
    setCreating(true);
    try {
      const d = await apiFetch('/api/product-tests', { method: 'POST', body: JSON.stringify({ name: n }) });
      setNewName('');
      load();
      // Abre direto o novo produto
      openDetail(d.id);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const d = await apiFetch(`/api/product-tests/${id}`);
      setSelected(d);
    } catch (err) {
      alert(String(err.message || err));
    }
  };

  const refreshDetail = async () => {
    if (!selected) return;
    openDetail(selected.id);
    load();
  };

  const byStatus = {};
  STATUSES.forEach(s => { byStatus[s.key] = products.filter(p => p.status === s.key); });

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div>
          <div style={s.title}>Produtos em Teste</div>
          <div style={s.subtitle}>Pipeline colaborativo dos produtos que a equipe esta testando. Kanban por status; click no card pra detalhes.</div>
        </div>
        <form onSubmit={create} style={{ display: 'flex', gap: 6 }}>
          <input placeholder="Novo produto..." value={newName} onChange={e => setNewName(e.target.value)} required style={s.input} />
          <button type="submit" disabled={creating} style={{ ...s.btn, opacity: creating ? 0.5 : 1 }}>+ Criar</button>
        </form>
      </div>

      <div style={s.filterBar}>
        <input placeholder="Buscar por nome/nicho..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...s.input, minWidth: 220 }} />
        <input placeholder="Filtrar nicho" value={nichoFilter} onChange={e => setNichoFilter(e.target.value)} style={{ ...s.input, width: 140 }} />
      </div>

      {error && <div style={s.errBox}>{error}</div>}
      {loading && <div style={s.dim}>Carregando...</div>}

      {!loading && (
        <div style={s.board}>
          {STATUSES.map(st => (
            <div key={st.key} style={{ ...s.col, background: st.bg, borderColor: st.color }}>
              <div style={{ ...s.colHeader, color: st.color }}>
                <span>{st.label}</span>
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>{byStatus[st.key].length}</span>
              </div>
              <div style={s.colBody}>
                {byStatus[st.key].map(p => (
                  <div key={p.id} onClick={() => openDetail(p.id)} style={s.card}>
                    <div style={s.cardName}>{p.name}</div>
                    {p.nicho && <div style={s.cardNicho}>{p.nicho}</div>}
                    <div style={s.cardMeta}>
                      {p.vsl_url && <span title="VSL cadastrada" style={s.miniChip}>VSL</span>}
                      {p.lp_url && <span title="LP cadastrada" style={s.miniChip}>LP</span>}
                      {(p.creative_urls || []).length > 0 && <span title={`${p.creative_urls.length} criativo(s)`} style={s.miniChip}>C×{p.creative_urls.length}</span>}
                      {p.pixel_id && <span title={`Pixel ${p.pixel_id}`} style={{ ...s.miniChip, background: 'rgba(67,97,238,0.15)', color: 'var(--accent)' }}>PIX</span>}
                    </div>
                  </div>
                ))}
                {byStatus[st.key].length === 0 && <div style={s.emptyCol}>—</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && <DetailPanel product={selected} onClose={() => setSelected(null)} onChange={refreshDetail} />}
    </div>
  );
}

// ============================================================================
// Detail side-panel
// ============================================================================
function DetailPanel({ product: p, onClose, onChange }) {
  const [tab, setTab] = useState('info');

  const patch = async (data) => {
    try { await apiFetch(`/api/product-tests/${p.id}`, { method: 'PATCH', body: JSON.stringify(data) }); onChange(); }
    catch (e) { alert(String(e.message || e)); }
  };
  const remove = async () => {
    if (!confirm('Arquivar este produto?')) return;
    try { await apiFetch(`/api/product-tests/${p.id}`, { method: 'DELETE' }); onChange(); onClose(); }
    catch (e) { alert(String(e.message || e)); }
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.panel} onClick={e => e.stopPropagation()}>
        <div style={s.panelHead}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <EditText value={p.name} onSave={v => patch({ name: v })} style={{ fontSize: 18, fontWeight: 700 }} />
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={p.status} onChange={e => patch({ status: e.target.value })} style={s.select}>
                {STATUSES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
              </select>
              <EditText value={p.nicho || ''} placeholder="nicho (ex: nutra US)" onSave={v => patch({ nicho: v || null })} style={{ fontSize: 12, color: 'var(--text2)' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={remove} style={{ ...s.btnGhost, color: 'var(--accent-r)' }}>Arquivar</button>
            <button onClick={onClose} style={s.btnGhost}>Fechar</button>
          </div>
        </div>

        <div style={s.tabsBar}>
          {['info', 'checklist', 'metrics'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{ ...s.tab, ...(tab === t ? s.tabOn : {}) }}>
              {t === 'info' ? 'Assets + Notas' : t === 'checklist' ? `Checklist (${p.checklist.filter(c => c.done).length}/${p.checklist.length})` : `Metricas (${p.metrics.length}d)`}
            </button>
          ))}
        </div>

        <div style={s.panelBody}>
          {tab === 'info' && <InfoTab product={p} patch={patch} />}
          {tab === 'checklist' && <ChecklistTab product={p} onChange={onChange} />}
          {tab === 'metrics' && <MetricsTab product={p} onChange={onChange} />}
        </div>
      </div>
    </div>
  );
}

function EditText({ value, onSave, placeholder, style }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || '');
  useEffect(() => setV(value || ''), [value]);
  if (!editing) return (
    <div onClick={() => setEditing(true)} style={{ ...(style || {}), cursor: 'pointer', color: v ? (style?.color || 'var(--text)') : 'var(--text3)' }}>
      {v || placeholder || '(clique pra editar)'}
    </div>
  );
  return (
    <input autoFocus value={v} onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (value || '')) onSave(v); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value || ''); setEditing(false); } }}
      style={{ ...s.input, ...(style || {}), padding: '4px 8px' }} />
  );
}

function InfoTab({ product: p, patch }) {
  const [creative, setCreative] = useState('');
  const [reference, setReference] = useState('');

  const addCreative = () => { if (creative.trim()) { patch({ creative_urls: [...(p.creative_urls || []), creative.trim()] }); setCreative(''); } };
  const rmCreative = (i) => patch({ creative_urls: p.creative_urls.filter((_, j) => j !== i) });
  const addReference = () => { if (reference.trim()) { patch({ reference_urls: [...(p.reference_urls || []), reference.trim()] }); setReference(''); } };
  const rmReference = (i) => patch({ reference_urls: p.reference_urls.filter((_, j) => j !== i) });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Field label="VSL URL" value={p.vsl_url} onSave={v => patch({ vsl_url: v || null })} />
      <Field label="LP URL"  value={p.lp_url}  onSave={v => patch({ lp_url:  v || null })} />

      <div>
        <div style={s.fieldLabel}>Criativos ({(p.creative_urls || []).length})</div>
        {(p.creative_urls || []).map((u, i) => (
          <div key={i} style={s.arrayRow}>
            <a href={u} target="_blank" rel="noopener" style={s.linkA}>{u}</a>
            <button onClick={() => rmCreative(i)} style={s.miniBtn}>×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <input placeholder="URL do criativo..." value={creative} onChange={e => setCreative(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCreative()} style={s.input} />
          <button onClick={addCreative} style={s.btnGhost}>+ Add</button>
        </div>
      </div>

      <div>
        <div style={s.fieldLabel}>Referencias / Concorrentes ({(p.reference_urls || []).length})</div>
        {(p.reference_urls || []).map((u, i) => (
          <div key={i} style={s.arrayRow}>
            <a href={u} target="_blank" rel="noopener" style={s.linkA}>{u}</a>
            <button onClick={() => rmReference(i)} style={s.miniBtn}>×</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <input placeholder="URL de referencia..." value={reference} onChange={e => setReference(e.target.value)} onKeyDown={e => e.key === 'Enter' && addReference()} style={s.input} />
          <button onClick={addReference} style={s.btnGhost}>+ Add</button>
        </div>
      </div>

      <div style={{ padding: 12, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <div style={{ ...s.fieldLabel, marginBottom: 8, color: 'var(--accent)' }}>Pixel FB / CAPI</div>
        <Field label="Pixel ID (numerico)" value={p.pixel_id} onSave={v => patch({ pixel_id: v || null })} />
        <div style={{ marginTop: 8 }}>
          <div style={s.fieldLabel}>Access Token (CAPI, opcional)</div>
          <EditPassword value={p.pixel_access_token || ''} onSave={v => patch({ pixel_access_token: v || null })} />
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={s.fieldLabel}>Notas pixel</div>
          <textarea key={p.id + '-pixel-notes'} defaultValue={p.pixel_notes || ''} onBlur={e => e.target.value !== (p.pixel_notes || '') && patch({ pixel_notes: e.target.value || null })}
            placeholder="Ex: BM 12345, eventos: PageView + Lead + Purchase..." rows={3} style={{ ...s.input, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
      </div>

      <div>
        <div style={s.fieldLabel}>Notas gerais</div>
        <textarea key={p.id + '-notes'} defaultValue={p.notes || ''} onBlur={e => e.target.value !== (p.notes || '') && patch({ notes: e.target.value || null })}
          placeholder="Contexto, briefing, decisoes..." rows={5} style={{ ...s.input, resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
    </div>
  );
}

function EditPassword({ value, onSave }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!editing) {
    const masked = value ? (show ? value : '•'.repeat(Math.min(value.length, 40))) : '(clique pra adicionar)';
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <div onClick={() => setEditing(true)} style={{ flex: 1, cursor: 'pointer', fontSize: 11, color: value ? 'var(--text)' : 'var(--text3)', fontFamily: value ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>{masked}</div>
        {value && <button onClick={() => setShow(!show)} style={s.miniBtn}>{show ? 'Ocultar' : 'Ver'}</button>}
      </div>
    );
  }
  return (
    <input autoFocus type="text" value={v} onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== value) onSave(v); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value); setEditing(false); } }}
      placeholder="EAAxxx... (token do CAPI)"
      style={{ ...s.input, width: '100%', fontFamily: 'monospace' }} />
  );
}

function Field({ label, value, onSave }) {
  return (
    <div>
      <div style={s.fieldLabel}>{label}</div>
      <EditText value={value || ''} placeholder="(clique pra adicionar)" onSave={onSave} style={{ fontSize: 12, color: 'var(--text)' }} />
    </div>
  );
}

function ChecklistTab({ product: p, onChange }) {
  const [newLabel, setNewLabel] = useState('');
  const add = async () => {
    if (!newLabel.trim()) return;
    try { await apiFetch(`/api/product-tests/${p.id}/checklist`, { method: 'POST', body: JSON.stringify({ label: newLabel.trim() }) }); setNewLabel(''); onChange(); }
    catch (e) { alert(String(e.message || e)); }
  };
  const toggle = async (item) => {
    try { await apiFetch(`/api/product-tests/checklist/${item.id}`, { method: 'PATCH', body: JSON.stringify({ done: !item.done }) }); onChange(); }
    catch (e) { alert(String(e.message || e)); }
  };
  const remove = async (item) => {
    if (!confirm(`Remover: ${item.label}?`)) return;
    try { await apiFetch(`/api/product-tests/checklist/${item.id}`, { method: 'DELETE' }); onChange(); }
    catch (e) { alert(String(e.message || e)); }
  };
  return (
    <div>
      {p.checklist.map(item => (
        <div key={item.id} style={s.checklistRow}>
          <input type="checkbox" checked={item.done} onChange={() => toggle(item)} />
          <span style={{ ...s.checklistLabel, ...(item.done ? { textDecoration: 'line-through', color: 'var(--text3)' } : {}) }}>{item.label}</span>
          <button onClick={() => remove(item)} style={s.miniBtn}>×</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input placeholder="ex: Pixel instalado, Checkout ok..." value={newLabel} onChange={e => setNewLabel(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} style={s.input} />
        <button onClick={add} style={s.btnGhost}>+ Add</button>
      </div>
    </div>
  );
}

function MetricsTab({ product: p, onChange }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [spend, setSpend] = useState('');
  const [conv, setConv] = useState('');
  const [rev, setRev] = useState('');
  const [notes, setNotes] = useState('');

  const upsert = async () => {
    const body = {
      date,
      spend_cents: Math.round(parseFloat(spend || '0') * 100),
      conversions: parseInt(conv || '0', 10),
      revenue_cents: Math.round(parseFloat(rev || '0') * 100),
      notes: notes || null,
    };
    try { await apiFetch(`/api/product-tests/${p.id}/metrics`, { method: 'PUT', body: JSON.stringify(body) }); setSpend(''); setConv(''); setRev(''); setNotes(''); onChange(); }
    catch (e) { alert(String(e.message || e)); }
  };

  const t = p.totals || {};

  return (
    <div>
      <div style={s.totalsRow}>
        <div style={s.stat}><div style={s.statLabel}>Total gasto</div><div style={s.statVal}>{centsToStr(t.spend_cents)}</div></div>
        <div style={s.stat}><div style={s.statLabel}>Total revenue</div><div style={s.statVal}>{centsToStr(t.revenue_cents)}</div></div>
        <div style={s.stat}><div style={s.statLabel}>Conversoes</div><div style={s.statVal}>{t.conversions ?? '—'}</div></div>
        <div style={s.stat}><div style={s.statLabel}>ROAS</div><div style={{ ...s.statVal, color: (t.roas != null && t.roas >= 1) ? 'var(--accent-g)' : 'var(--accent-r)' }}>{t.roas ?? '—'}</div></div>
      </div>

      <div style={{ ...s.form, marginTop: 12, flexWrap: 'wrap' }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...s.input, width: 140 }} />
        <input placeholder="Gasto R$" type="number" step="0.01" value={spend} onChange={e => setSpend(e.target.value)} style={{ ...s.input, width: 110 }} />
        <input placeholder="Conv" type="number" value={conv} onChange={e => setConv(e.target.value)} style={{ ...s.input, width: 80 }} />
        <input placeholder="Revenue R$" type="number" step="0.01" value={rev} onChange={e => setRev(e.target.value)} style={{ ...s.input, width: 110 }} />
        <input placeholder="Notas do dia" value={notes} onChange={e => setNotes(e.target.value)} style={{ ...s.input, minWidth: 200, flex: 1 }} />
        <button onClick={upsert} style={s.btn}>Salvar dia</button>
      </div>

      <table style={{ ...s.table, marginTop: 14 }}>
        <thead>
          <tr>
            <th style={s.th}>Data</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Gasto</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Conv</th>
            <th style={{ ...s.th, textAlign: 'right' }}>Revenue</th>
            <th style={{ ...s.th, textAlign: 'right' }}>ROAS</th>
            <th style={s.th}>Notas</th>
          </tr>
        </thead>
        <tbody>
          {p.metrics.map(m => {
            const roas = m.spend_cents ? (m.revenue_cents / m.spend_cents).toFixed(2) : '—';
            return (
              <tr key={m.id}>
                <td style={s.td}>{m.date}</td>
                <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{centsToStr(m.spend_cents)}</td>
                <td style={{ ...s.td, textAlign: 'right' }}>{m.conversions || 0}</td>
                <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{centsToStr(m.revenue_cents)}</td>
                <td style={{ ...s.td, textAlign: 'right', color: roas !== '—' && parseFloat(roas) >= 1 ? 'var(--accent-g)' : 'var(--text2)' }}>{roas}</td>
                <td style={{ ...s.td, color: 'var(--text3)' }}>{m.notes || '—'}</td>
              </tr>
            );
          })}
          {p.metrics.length === 0 && <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: 'var(--text3)', padding: 20 }}>Nenhuma metrica ainda. Registre o 1o dia acima.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================
const s = {
  page: { padding: 24, maxWidth: 1600, margin: '0 auto' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, marginBottom: 16, flexWrap: 'wrap' },
  title: { fontSize: 18, fontWeight: 700, color: 'var(--text)' },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4, maxWidth: 640 },
  filterBar: { display: 'flex', gap: 8, marginBottom: 16 },
  input: { padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit', outline: 'none' },
  select: { padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface2)', color: 'var(--text)', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  btn: { padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost: { padding: '6px 12px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
  miniBtn: { padding: '2px 6px', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 4, fontSize: 10, cursor: 'pointer' },
  errBox: { padding: 10, background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', borderRadius: 6, fontSize: 12, marginBottom: 10 },
  dim: { fontSize: 12, color: 'var(--text3)', padding: 20 },

  board: { display: 'grid', gridTemplateColumns: 'repeat(6, minmax(180px, 1fr))', gap: 8 },
  col: { border: '1px solid', borderRadius: 8, padding: 8, minHeight: 300 },
  colHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, padding: '0 4px' },
  colBody: { display: 'flex', flexDirection: 'column', gap: 6 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 10, cursor: 'pointer' },
  cardName: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  cardNicho: { fontSize: 10, color: 'var(--text3)', marginTop: 2 },
  cardMeta: { display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' },
  miniChip: { fontSize: 9, padding: '1px 5px', background: 'var(--surface2)', color: 'var(--text3)', borderRadius: 3, fontWeight: 600 },
  emptyCol: { textAlign: 'center', color: 'var(--text3)', fontSize: 20, padding: 20 },

  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' },
  panel: { width: '100%', maxWidth: 720, background: 'var(--bg)', borderLeft: '1px solid var(--border)', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  panelHead: { padding: 20, borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'flex-start' },
  panelBody: { padding: 20, flex: 1 },
  tabsBar: { display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface)' },
  tab: { padding: '10px 16px', background: 'transparent', border: 'none', color: 'var(--text3)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', borderBottom: '2px solid transparent' },
  tabOn: { color: 'var(--accent)', borderBottomColor: 'var(--accent)' },

  fieldLabel: { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 },
  arrayRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid var(--surface3)' },
  linkA: { color: 'var(--accent)', textDecoration: 'none', fontSize: 11, wordBreak: 'break-all', flex: 1 },

  checklistRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: '1px solid var(--surface3)' },
  checklistLabel: { flex: 1, fontSize: 13, color: 'var(--text)' },

  totalsRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  stat: { padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, minWidth: 120 },
  statLabel: { fontSize: 9, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 },
  statVal: { fontSize: 16, fontWeight: 700, color: 'var(--text)', marginTop: 2 },

  form: { display: 'flex', gap: 6, alignItems: 'center' },
  table: { width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)', fontWeight: 600 },
  td: { padding: '8px 12px', borderBottom: '1px solid var(--surface3)', fontSize: 12, color: 'var(--text)' },
};
